import { FC, useState, useEffect, useContext } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import './SlideInOverlay.scss';
import { SlideInHeader } from './SlideInHeader/SlideInHeader';
import { SlideInBody } from './SlideInBody/SlideInBody';
import {
	useCreateStoreOrder,
	useCancelStoreOrder,
	useCraftLogger,
} from '../../Hooks/OrderBookHooks';
import { LocationContext } from '../../../Core/Contexts/LocationContext';
import { StoreOrder, FormErrors } from '../../../Core/SharedTypes/Models';

export type Props = {
	orderData: StoreOrder | null;
	isOpen: boolean;
	setIsOpen: () => void;
};

export const SlideInOverlay: FC<Props> = ({ orderData, isOpen, setIsOpen }) => {
	const selectedLocation = useContext(LocationContext);
	const { mutate: log } = useCraftLogger();

	const defaultOrder: StoreOrder = {
		customerName: '',
		attention: '',
		phone: '',
		pickupTime: '',
		deliveryType: '',
		locationId: selectedLocation[0] ?? 0,
		receiverName: '',
		paidStatus: false,
		packedStatus: false,
		status: false,
		lineItems: [
			{
				id: 1,
				orderId: 0,
				description: '',
				qty: 1,
				planning: 0,
				plannedInPastry: 0,
				plannedInBakery: 0,
				approved: 0,
				preparedBakery: 0,
				preparedPastry: 0,
			},
		],
	};

	const [formValues, setFormValues] = useState<StoreOrder | null>(defaultOrder);
	const [formErrors, setFormErrors] = useState<FormErrors>({
		lineItems: '',
		customerName: '',
		receiverName: '',
	});

	useEffect(() => {
		setFormValues(orderData ? { ...orderData } : { ...defaultOrder }); // Always create a fresh copy
	}, [orderData]);

	// Callback to update state from child components
	const handleFormChange = (updatedValues: any) => {
		setFormValues((prevState) => ({
			...prevState,
			...updatedValues,
		}));
	};

	const handleSlideIn = () => {
		setIsOpen();
		setFormErrors({
			lineItems: '',
			customerName: '',
			receiverName: '',
		});
	};

	// Submitted order
	const { createStoreOrder, isSaving } = useCreateStoreOrder();

	const handleSubmit = async () => {
		if (isSaving) return;

		const errors: FormErrors = {};

		// Check for lineItem errors
		if (!formValues?.customerName) {
			errors.customerName = 'Kunde navn mangler.';
		}

		if (!formValues?.receiverName) {
			errors.receiverName = 'Modtager navn mangler.';
		}

		if (!formValues?.deliveryType) {
			errors.deliveryType = 'Leveringsmetode mangler.';
		}

		if (
			formValues?.pickupTime.includes('Vælg') ||
			formValues?.pickupTime === ''
		) {
			errors.pickupTime = 'Afhentningsdato eller afhentningstid ikke sat.';
		}

		if (!formValues?.lineItems.length) {
			errors.lineItems =
				'Bestillingslinje mangler. Tilføj venligst mindst én bestillingslinje.';
		}

		if (formValues?.lineItems.some((lineItem) => !lineItem.description)) {
			errors.lineItems =
				'Beskrivelse mangler for en eller flere bestillingslinjer.';
		}

		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
			return;
		}

		try {
			await createStoreOrder(formValues);
			toast.success('Ordre gemt!');

			setTimeout(() => {
				handleSlideIn();
				setFormValues(defaultOrder);
			}, 900);
		} catch (error) {
			var errorMessage =
				error instanceof Error ? error.message : 'En ukendt fejl opstod.';
			toast.error('Ordre kunne ikke gemmes;\n' + errorMessage);
			log({ type: 'error', message: `Form creation failed: ${error}` });
		}
	};

	// Cancelled order
	const { cancelStoreOrder, isCancelling } = useCancelStoreOrder();

	const handleCancellation = async (orderNumber: string) => {
		// Prevent double submission
		if (isCancelling) return;

		const result = await cancelStoreOrder(orderNumber);

		if (formValues) {
			formValues.dateCancelled = result.data.result.dateCancelled;
			handleFormChange({ ...formValues });
		}
	};

	const headerButtonText = orderData ? 'Gem ordre' : 'Opret';

	return (
		<div
			className={`c-slideInOverlay ${isOpen ? 'c-slideInOverlay--open' : ''}`}
		>
			<Toaster />
			<div
				className="c-slideInOverlay__wrapper"
				onClick={() => handleSlideIn()}
			>
				<div className="content" onClick={(e) => e.stopPropagation()}>
					<SlideInHeader
						isSaving={isSaving}
						buttonText={headerButtonText}
						formValues={formValues}
						handleSlideIn={handleSlideIn}
						handleSubmit={handleSubmit}
					/>
					<SlideInBody
						onFormChange={handleFormChange}
						onOrderCancelled={(orderNumber) => handleCancellation(orderNumber)}
						formValues={formValues}
						isSaving={isSaving}
						isCancelling={isCancelling}
						formErrors={formErrors}
					/>
				</div>
			</div>
		</div>
	);
};
